import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Loader2, AlertTriangle } from 'lucide-react';

export const QuotaDeleteDialog = ({ open, quota, onClose, onQuotaDeleted }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    if (!quota) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`/admin/quotas/${quota.id}`, {
        method: 'DELETE',
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || t('Deleting the quota failed'));
      }

      if (json.success) {
        onQuotaDeleted();
      } else {
        throw new Error(json.error || t('Deleting the quota failed'));
      }
    } catch (err) {
      console.error('Error deleting quota:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!quota) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            {t('Delete the quota')}
          </DialogTitle>
          <DialogDescription>
            {t('Are you sure you want to delete this quota rule? This action cannot be undone.')}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="font-medium text-gray-700">{t('Service Desk')}:</div>
            <div className="text-gray-900">{quota.serviceDeskName}</div>

            <div className="font-medium text-gray-700">Request Type ID:</div>
            <div className="text-gray-900">{quota.scope.requestTypeId}</div>

            <div className="font-medium text-gray-700">{t('Limit')}:</div>
            <div className="text-gray-900">{quota.limit}</div>

            <div className="font-medium text-gray-700">{t('Status')}:</div>
            <div className="text-gray-900">
              {quota.enabled ? (
                <span className="text-green-600 font-medium">{t('Active')}</span>
              ) : (
                <span className="text-gray-400">{t('Inactive')}</span>
              )}
            </div>
          </div>

          {quota.filterJQL && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="font-medium text-gray-700 text-sm mb-1">{t('JQL filter:')}</div>
              <div className="text-xs font-mono text-gray-600 bg-white p-2 rounded border border-gray-200 break-all">
                {quota.filterJQL}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('Delete permanently')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
